// Copyright (C) 2020-2021 Intel Corporation
//
// SPDX-License-Identifier: MIT

import React, { useMemo, useCallback } from 'react';
import CVATTooltip from 'components/common/cvat-tooltip';
import Icon from '@ant-design/icons';

import { Canvas } from 'cvat-canvas-wrapper';
import { ShapeType, ActiveControl } from 'reducers/interfaces';
import withVisibilityHandling from './handle-popover-visibility';

export interface Props {
    canvasInstance: Canvas;
    activeControl: ActiveControl;
    /** the label id currently active in Redux (persisted by rememberObject) */
    activeLabelID?: number;
    disabled?: boolean;
    labels: any[];
    /** Label this control should draw with (by name) */
    labelName: string;
    // Icon to render for this label (use a distinct icon per label)
    iconComponent: React.ComponentType;
    // Optional: show shortcut hint inside tooltip text, e.g. "Shift+1"
    shortcutHint?: string;
    /** Optional callback when we actually start drawing (for analytics, etc.) */
    onStarted?: (labelId: number) => void;
    /** Optional callback when we stop/cancel drawing */
    onStopped?: () => void;
    onRelabelSelected?: (labelId: number) => boolean;
}

/**
 * A compact control that starts polygon drawing with a preset label.
 * - When inactive, clicking enters polygon drawing with the given label pre-selected.
 */
function QuickPolygonLabelControl(props: Props): JSX.Element {
    const {
        canvasInstance,
        activeControl,
        activeLabelID,
        disabled = false,
        labels,
        labelName,
        iconComponent,
        shortcutHint,
        onStarted,
        onStopped,
        onRelabelSelected,
    } = props;

    const normalize = (s: string) =>
        String(s || '')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '');

    // find label + color safely
    const label = useMemo(() => {
        const target = normalize(labelName);
        return (labels || []).find((l: any) => normalize(l?.name) === target);
    }, [labels, labelName]);

    const labelId = label?.id as number | undefined;

    // small alpha helper for hex colors: "#rrggbb" -> rgba with alpha
    const withAlpha = (hex: string, alpha = 0.53) => {
        const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
        if (!m) return `rgba(241,241,241,${alpha})`;
        const r = parseInt(m[1], 16);
        const g = parseInt(m[2], 16);
        const b = parseInt(m[3], 16);
        return `rgba(${r},${g},${b},${alpha})`;
    };

    const bgColor = withAlpha(label?.color || '#f1f1f1', 0.53);

    // Disabled if no labels or this specific label could not be found
    const isDisabled = disabled || typeof labelId !== 'number';

    const isDrawing =
        activeControl === ActiveControl.DRAW_POLYGON && typeof activeLabelID === 'number' && activeLabelID === labelId;

    const className = [
        'cvat-quick-polygon-label-control',
        isDrawing ? 'cvat-active-canvas-control' : '',
        isDisabled ? 'cvat-disabled-canvas-control' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const title = shortcutHint
        ? `Draw ${ShapeType.POLYGON.toLowerCase()}: ${labelName} (${shortcutHint})`
        : `Draw ${ShapeType.POLYGON.toLowerCase()}: ${labelName}`;

    const stopDrawing = useCallback(() => {
        canvasInstance.draw({ enabled: false });
        onStopped?.();
    }, [canvasInstance, onStopped]);

    const startDrawing = useCallback(() => {
        if (isDisabled || !labelId) return;

        // Cancel any other mode (mirrors other controls)
        canvasInstance.cancel();

        // Start polygon drawing for this label
        // IMPORTANT: For polygon we do not set `crosshair` (only used for rectangle/cuboid in CVAT UI)
        // Pass both `label` and `labelId` for compatibility across CVAT builds.
        canvasInstance.draw({
            enabled: true,
            shapeType: ShapeType.POLYGON,
            label: labelId,
            labelId,
        });

        onStarted?.(labelId);
    }, [canvasInstance, isDisabled, labelId, onStarted]);

    const onClick = (): void => {
        if (isDisabled) return;

        // 1) Try relabel selected/highlighted shapes first
        if (labelId && onRelabelSelected && onRelabelSelected(labelId)) {
            return; // relabeled; do not start drawing
        }

        // 2) Otherwise toggle drawing for this label
        if (isDrawing) {
            stopDrawing();
        } else {
            startDrawing();
        }
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
        if (isDisabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
        }
    };

    return (
        <CVATTooltip title={title} placement='right'>
            <div
                role='button'
                tabIndex={isDisabled ? -1 : 0}
                aria-disabled={isDisabled}
                onClick={isDisabled ? undefined : onClick}
                onKeyDown={onKeyDown}
                className={className}
                style={{
                    '--label-color': label?.color || '#ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 4,
                    background: bgColor,
                    boxShadow: isDrawing ? '0 0 0 2px rgba(0,0,0,0.2) inset' : undefined,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.5 : 1,
                    userSelect: 'none',
                    outline: 'none',
                }}
            >
                {/* keep original icon classes so existing CSS still applies */}
                <Icon component={iconComponent as any} />
            </div>
        </CVATTooltip>
    );
}

export const ObservedQuickPolygonLabelControl = withVisibilityHandling(
    React.memo(QuickPolygonLabelControl),
    'quick-polygon-label-control',
);

export default React.memo(QuickPolygonLabelControl);
